import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Select,
	Tab,
	Tabs,
	TextField,
	Tooltip,
} from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiBookOpenPageVariant,
	mdiPlus,
	mdiPencil,
	mdiDelete,
	mdiEye,
	mdiTagOutline,
	mdiFolderOutline,
	mdiMagnify,
	mdiClockOutline,
	mdiCheckCircleOutline,
	mdiBrain,
	mdiRefresh,
	mdiDownload,
	mdiUpload,
	mdiArchiveOutline,
	mdiDragVertical,
	mdiChevronRight,
	mdiChevronDown,
} from "@mdi/js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
	KnowledgeBaseServices,
	NotistackServices,
	PublicKbArticleDTO,
	PublicKbCategoryDTO,
	PublicKbTagDTO,
	SemanticArticleResult,
} from "shared/services";
import { useAccount, useWorkspaceData } from "shared/hooks";
import { HandleError } from "shared/utils";
import { MainLayout } from "layouts";
import styles from "./styles.module.scss";

/**
 * EK-421 — Knowledge Base page.
 * Tabs: Browse (leitura) | Manage (admin para users com permissao)
 */

type Tab = "browse" | "manage";

export const KnowledgeBasePage: React.FC = () => {
	const user = useAccount();
	const { workspace, loadingWorkspace } = useWorkspaceData();
	const [tab, setTab] = useState<Tab>("browse");
	const canManage = (user?.permissionLevel ?? 0) >= 1; // MANAGER+

	if (loadingWorkspace) {
		return (
			<MainLayout>
				<div className={styles.loading}><CircularProgress /></div>
			</MainLayout>
		);
	}

	if (!workspace) {
		return (
			<MainLayout>
				<div className={styles.empty}>Workspace nao encontrado.</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<div className={styles.page}>
				<div className={styles.header}>
					<div>
						<h1 className={styles.title}>
							<Icon path={mdiBookOpenPageVariant} size={1.1} /> Base de Conhecimento
						</h1>
						<p className={styles.subtitle}>Workspace: <strong>{workspace.name}</strong></p>
					</div>
					{canManage && (
						<Tabs value={tab} onChange={(_e, v) => setTab(v)}>
							<Tab value="browse" label="Browse" />
							<Tab value="manage" label="Manage" />
						</Tabs>
					)}
				</div>

				{tab === "browse" && <BrowseSection workspaceId={String(workspace.id)} />}
				{tab === "manage" && canManage && <ManageSection workspaceId={String(workspace.id)} />}
			</div>
		</MainLayout>
	);
};

// ── Browse Section ─────────────────────────────────────────────────
type ArticleListItem = PublicKbArticleDTO & { similarity?: number };

const BrowseSection: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
	const [categories, setCategories] = useState<PublicKbCategoryDTO[]>([]);
	const [tags, setTags] = useState<PublicKbTagDTO[]>([]);
	const [articles, setArticles] = useState<ArticleListItem[]>([]);
	const [selectedArticle, setSelectedArticle] = useState<PublicKbArticleDTO | null>(null);
	const [search, setSearch] = useState("");
	const [semantic, setSemantic] = useState(true); // EK-422: default ON
	const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
	const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);

	const fetchSidebar = useCallback(async () => {
		try {
			const [cats, tagList] = await Promise.all([
				KnowledgeBaseServices.listCategories(workspaceId),
				KnowledgeBaseServices.listTags(workspaceId),
			]);
			setCategories(cats);
			setTags(tagList);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao carregar KB");
		}
	}, [workspaceId]);

	const fetchArticles = useCallback(async () => {
		setLoading(true);
		try {
			const trimmed = search.trim();
			// EK-422: quando busca textual + modo semantico + nao tem filtro de categoria/tag
			// (semantic so faz sentido p/ query natural; filtros estruturais usam list normal).
			if (semantic && trimmed.length >= 3 && !selectedCategoryId && !selectedTagId) {
				const res = await KnowledgeBaseServices.searchArticlesSemantic(workspaceId, trimmed, {
					limit: 20,
					minSimilarity: 0.2,
					includeTags: true,
					includeCategory: true,
				});
				setArticles(res.items);
			} else {
				const res = await KnowledgeBaseServices.listArticles(workspaceId, {
					status: "published",
					categoryId: selectedCategoryId ?? undefined,
					tagId: selectedTagId ?? undefined,
					search: !semantic && trimmed ? trimmed : undefined,
					includeTags: true,
					includeCategory: true,
					limit: 50,
				});
				setArticles(res.items);
			}
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao listar artigos");
		} finally {
			setLoading(false);
		}
	}, [workspaceId, selectedCategoryId, selectedTagId, search, semantic]);

	useEffect(() => { void fetchSidebar(); }, [fetchSidebar]);
	// Debounce 400ms p/ evitar chamar embedding API a cada tecla
	useEffect(() => {
		const t = setTimeout(() => { void fetchArticles(); }, 400);
		return () => clearTimeout(t);
	}, [fetchArticles]);

	const handleOpenArticle = async (slug: string) => {
		try {
			const art = await KnowledgeBaseServices.getArticleBySlug(workspaceId, slug);
			setSelectedArticle(art);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao abrir artigo");
		}
	};

	if (selectedArticle) {
		return (
			<div className={styles.articleView}>
				<Button onClick={() => setSelectedArticle(null)} size="small">← Voltar</Button>
				<h1 className={styles.articleTitle}>{selectedArticle.title}</h1>
				<div className={styles.articleMeta}>
					<span><Icon path={mdiClockOutline} size={0.7} /> {selectedArticle.readingTimeMinutes} min</span>
					<span><Icon path={mdiEye} size={0.7} /> {selectedArticle.viewCount} views</span>
					{selectedArticle.category && (
						<Chip size="small" icon={<Icon path={mdiFolderOutline} size={0.6} />} label={selectedArticle.category.name} />
					)}
					{(selectedArticle.tags ?? []).map((t) => (
						<Chip key={t.id} size="small" label={t.name} style={{ backgroundColor: t.color ?? undefined }} />
					))}
				</div>
				<div className={styles.articleBody}>
					<ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedArticle.content}</ReactMarkdown>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.browseGrid}>
			<aside className={styles.sidebar}>
				<div className={styles.searchBox}>
					<TextField
						size="small"
						fullWidth
						placeholder={semantic ? "Pergunte em linguagem natural..." : "Buscar literal..."}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: <Icon path={semantic ? mdiBrain : mdiMagnify} size={0.8} style={{ marginRight: 8 }} />,
						}}
					/>
					<Tooltip title={semantic ? "Modo semantico (IA): clique p/ usar busca literal" : "Modo literal: clique p/ usar busca semantica (IA)"}>
						<Chip
							size="small"
							icon={<Icon path={mdiBrain} size={0.6} />}
							label={semantic ? "IA ON" : "IA OFF"}
							color={semantic ? "primary" : "default"}
							onClick={() => setSemantic((v) => !v)}
							style={{ marginTop: 6, cursor: "pointer" }}
						/>
					</Tooltip>
				</div>

				<div className={styles.sidebarSection}>
					<h4>Categorias</h4>
					<button className={`${styles.filterItem} ${!selectedCategoryId ? styles.active : ""}`} onClick={() => setSelectedCategoryId(null)}>
						Todas
					</button>
					{categories.map((cat) => (
						<CategoryNode key={cat.id} category={cat} selectedId={selectedCategoryId} onSelect={setSelectedCategoryId} depth={0} />
					))}
				</div>

				<div className={styles.sidebarSection}>
					<h4>Tags</h4>
					<div className={styles.tagList}>
						{tags.map((t) => (
							<Chip
								key={t.id}
								label={t.name}
								size="small"
								onClick={() => setSelectedTagId(t.id === selectedTagId ? null : t.id)}
								variant={t.id === selectedTagId ? "filled" : "outlined"}
								style={{ backgroundColor: t.id === selectedTagId ? t.color ?? undefined : undefined }}
							/>
						))}
						{tags.length === 0 && <p className={styles.emptyText}>Nenhuma tag ainda.</p>}
					</div>
				</div>
			</aside>

			<main className={styles.articleList}>
				{loading ? (
					<div className={styles.loading}><CircularProgress /></div>
				) : articles.length === 0 ? (
					<div className={styles.empty}>Nenhum artigo publicado ainda.</div>
				) : (
					articles.map((a) => (
						<div key={a.id} className={styles.articleCard} onClick={() => handleOpenArticle(a.slug)}>
							<h3>{a.title}</h3>
							{a.excerpt && <p className={styles.excerpt}>{a.excerpt}</p>}
							<div className={styles.cardFooter}>
								{a.similarity !== undefined && (
									<Chip
										size="small"
										icon={<Icon path={mdiBrain} size={0.6} />}
										label={`${Math.round(a.similarity * 100)}% match`}
										color="primary"
										variant="outlined"
									/>
								)}
								<span><Icon path={mdiClockOutline} size={0.6} /> {a.readingTimeMinutes} min</span>
								<span><Icon path={mdiEye} size={0.6} /> {a.viewCount}</span>
								{a.category && (
									<Chip size="small" label={a.category.name} variant="outlined" />
								)}
								{(a.tags ?? []).slice(0, 3).map((t) => (
									<Chip key={t.id} size="small" label={t.name} style={{ backgroundColor: t.color ?? undefined }} />
								))}
							</div>
						</div>
					))
				)}
			</main>
		</div>
	);
};

const CategoryNode: React.FC<{ category: PublicKbCategoryDTO; selectedId: string | null; onSelect: (id: string) => void; depth: number }> = ({
	category,
	selectedId,
	onSelect,
	depth,
}) => {
	return (
		<>
			<button
				className={`${styles.filterItem} ${category.id === selectedId ? styles.active : ""}`}
				onClick={() => onSelect(category.id)}
				style={{ paddingLeft: 8 + depth * 12 }}
			>
				<Icon path={mdiFolderOutline} size={0.7} /> {category.name}
				{category.articleCount !== undefined && category.articleCount > 0 && (
					<span className={styles.count}>{category.articleCount}</span>
				)}
			</button>
			{(category.children ?? []).map((child) => (
				<CategoryNode key={child.id} category={child} selectedId={selectedId} onSelect={onSelect} depth={depth + 1} />
			))}
		</>
	);
};

// ── Manage Section (admin) — EK-424 ────────────────────────────────
type AdminSubTab = "articles" | "categories" | "tags";

const ManageSection: React.FC<{ workspaceId: string }> = ({ workspaceId }) => {
	const [subTab, setSubTab] = useState<AdminSubTab>("articles");
	const [articles, setArticles] = useState<PublicKbArticleDTO[]>([]);
	const [categories, setCategories] = useState<PublicKbCategoryDTO[]>([]);
	const [tags, setTags] = useState<PublicKbTagDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [editing, setEditing] = useState<{ kind: "article" | "category" | "tag"; data?: any } | null>(null);

	const refresh = useCallback(async () => {
		setLoading(true);
		try {
			const [arts, cats, tagList] = await Promise.all([
				KnowledgeBaseServices.listArticles(workspaceId, { limit: 200, includeCategory: true, includeTags: true }),
				KnowledgeBaseServices.listCategories(workspaceId),
				KnowledgeBaseServices.listTags(workspaceId),
			]);
			setArticles(arts.items);
			setCategories(cats);
			setTags(tagList);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao carregar KB");
		} finally {
			setLoading(false);
		}
	}, [workspaceId]);

	useEffect(() => { void refresh(); }, [refresh]);

	if (loading) return <div className={styles.loading}><CircularProgress /></div>;

	return (
		<div className={styles.manageGrid}>
			<Tabs value={subTab} onChange={(_e, v) => setSubTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
				<Tab value="articles" label={`Artigos (${articles.length})`} />
				<Tab value="categories" label={`Categorias (${categories.length})`} />
				<Tab value="tags" label={`Tags (${tags.length})`} />
			</Tabs>

			{subTab === "articles" && (
				<ArticlesAdmin
					workspaceId={workspaceId}
					articles={articles}
					categories={categories.flatMap((c) => flatCats(c))}
					tags={tags}
					onEdit={(a) => setEditing({ kind: "article", data: a })}
					onNew={() => setEditing({ kind: "article", data: null })}
					onRefresh={refresh}
				/>
			)}
			{subTab === "categories" && (
				<CategoriesAdmin
					workspaceId={workspaceId}
					categories={categories}
					onEdit={(c) => setEditing({ kind: "category", data: c })}
					onNew={() => setEditing({ kind: "category", data: null })}
					onRefresh={refresh}
				/>
			)}
			{subTab === "tags" && (
				<TagsAdmin
					workspaceId={workspaceId}
					tags={tags}
					onEdit={(t) => setEditing({ kind: "tag", data: t })}
					onNew={() => setEditing({ kind: "tag", data: null })}
					onRefresh={refresh}
				/>
			)}

			{editing && <EditorDialog workspaceId={workspaceId} editing={editing} categories={categories.flatMap(flatCats)} tags={tags} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
		</div>
	);
};

// ── Articles admin: bulk + export/import + checkbox selection ──────
const ArticlesAdmin: React.FC<{
	workspaceId: string;
	articles: PublicKbArticleDTO[];
	categories: (PublicKbCategoryDTO & { _depth: number })[];
	tags: PublicKbTagDTO[];
	onEdit: (a: PublicKbArticleDTO) => void;
	onNew: () => void;
	onRefresh: () => void;
}> = ({ workspaceId, articles, categories, tags, onEdit, onNew, onRefresh }) => {
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "published" | "archived">("all");
	const [busy, setBusy] = useState(false);

	const filtered = articles.filter((a) => statusFilter === "all" || a.status === statusFilter);
	const allSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));

	const toggleAll = () => {
		if (allSelected) {
			setSelected(new Set());
		} else {
			setSelected(new Set(filtered.map((a) => a.id)));
		}
	};

	const toggleOne = (id: string) => {
		const next = new Set(selected);
		if (next.has(id)) next.delete(id); else next.add(id);
		setSelected(next);
	};

	const bulk = async (action: "publish" | "archive" | "delete") => {
		if (selected.size === 0) return;
		if (action === "delete" && !window.confirm(`Excluir ${selected.size} artigos?`)) return;
		setBusy(true);
		try {
			const r = await KnowledgeBaseServices.bulkArticleAction(workspaceId, Array.from(selected), action);
			NotistackServices.showSuccessMessage(`${r.affected} artigos afetados (${action})`);
			setSelected(new Set());
			onRefresh();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no bulk");
		} finally {
			setBusy(false);
		}
	};

	const exportKB = async () => {
		setBusy(true);
		try {
			const data = await KnowledgeBaseServices.exportWorkspace(workspaceId);
			const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `kb-export-${workspaceId.slice(0, 8)}-${new Date().toISOString().slice(0, 10)}.json`;
			link.click();
			URL.revokeObjectURL(url);
			NotistackServices.showSuccessMessage(`Exportado: ${data.articles.length} artigos`);
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no export");
		} finally {
			setBusy(false);
		}
	};

	const importKB = async (file: File, mode: "merge" | "replace") => {
		setBusy(true);
		try {
			const text = await file.text();
			const data = JSON.parse(text);
			const r = await KnowledgeBaseServices.importWorkspace(workspaceId, data, mode);
			NotistackServices.showSuccessMessage(`Import: ${r.articles} artigos, ${r.categories} cats, ${r.tags} tags, ${r.skipped} ignorados`);
			onRefresh();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no import (arquivo invalido?)");
		} finally {
			setBusy(false);
		}
	};

	const fileInputRef = React.useRef<HTMLInputElement>(null);

	return (
		<section className={styles.manageSection}>
			<div className={styles.manageHeader}>
				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}>
						<MenuItem value="all">Todos</MenuItem>
						<MenuItem value="draft">Draft</MenuItem>
						<MenuItem value="published">Published</MenuItem>
						<MenuItem value="archived">Archived</MenuItem>
					</Select>
					{selected.size > 0 && (
						<>
							<span style={{ fontSize: "0.85rem", color: "var(--mui-palette-text-secondary)" }}>{selected.size} selecionados</span>
							<Button size="small" variant="outlined" color="success" disabled={busy} onClick={() => bulk("publish")} startIcon={<Icon path={mdiCheckCircleOutline} size={0.7} />}>Publicar</Button>
							<Button size="small" variant="outlined" disabled={busy} onClick={() => bulk("archive")} startIcon={<Icon path={mdiArchiveOutline} size={0.7} />}>Arquivar</Button>
							<Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => bulk("delete")} startIcon={<Icon path={mdiDelete} size={0.7} />}>Excluir</Button>
						</>
					)}
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					<input
						ref={fileInputRef}
						type="file"
						accept="application/json"
						style={{ display: "none" }}
						onChange={async (e) => {
							const f = e.target.files?.[0];
							if (!f) return;
							const mode = window.confirm("Modo REPLACE? OK = replace (apaga tudo antes), Cancel = merge (skip duplicados)") ? "replace" : "merge";
							await importKB(f, mode);
							e.target.value = "";
						}}
					/>
					<Tooltip title="Importar JSON (.json exportado da KB)">
						<Button size="small" variant="outlined" disabled={busy} onClick={() => fileInputRef.current?.click()} startIcon={<Icon path={mdiUpload} size={0.7} />}>Import</Button>
					</Tooltip>
					<Tooltip title="Exportar toda KB como JSON">
						<Button size="small" variant="outlined" disabled={busy} onClick={exportKB} startIcon={<Icon path={mdiDownload} size={0.7} />}>Export</Button>
					</Tooltip>
					<Tooltip title="Regenera embeddings p/ todos artigos (busca semantica)">
						<Button size="small" variant="outlined" disabled={busy} startIcon={<Icon path={mdiRefresh} size={0.7} />} onClick={async () => {
							try {
								const r = await KnowledgeBaseServices.backfillEmbeddings(workspaceId);
								NotistackServices.showSuccessMessage(`Reindexacao agendada: ${r.scheduled} artigos`);
							} catch (e) {
								NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro no backfill");
							}
						}}>Reindexar</Button>
					</Tooltip>
					<Button size="small" variant="contained" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={onNew}>Novo</Button>
				</div>
			</div>
			<table className={styles.manageTable}>
				<thead>
					<tr>
						<th style={{ width: 36 }}>
							<Checkbox size="small" checked={allSelected} indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} />
						</th>
						<th>Título</th>
						<th>Status</th>
						<th>Categoria</th>
						<th>Tags</th>
						<th>Views</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					{filtered.map((a) => (
						<tr key={a.id}>
							<td><Checkbox size="small" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} /></td>
							<td>{a.title}</td>
							<td>
								<Chip size="small" label={a.status} color={a.status === "published" ? "success" : a.status === "draft" ? "warning" : "default"} variant="outlined" />
							</td>
							<td>{a.category?.name ?? "—"}</td>
							<td>{(a.tags ?? []).map((t) => t.name).join(", ") || "—"}</td>
							<td className={styles.mono}>{a.viewCount}</td>
							<td>
								<IconButton size="small" onClick={() => onEdit(a)}><Icon path={mdiPencil} size={0.7} /></IconButton>
								{a.status !== "published" && (
									<Tooltip title="Publicar"><IconButton size="small" onClick={async () => {
										await KnowledgeBaseServices.publishArticle(workspaceId, a.id);
										NotistackServices.showSuccessMessage("Artigo publicado");
										onRefresh();
									}}><Icon path={mdiCheckCircleOutline} size={0.7} /></IconButton></Tooltip>
								)}
								<IconButton size="small" onClick={async () => {
									if (!window.confirm(`Excluir "${a.title}"?`)) return;
									await KnowledgeBaseServices.deleteArticle(workspaceId, a.id);
									onRefresh();
								}}><Icon path={mdiDelete} size={0.7} /></IconButton>
							</td>
						</tr>
					))}
					{filtered.length === 0 && (
						<tr><td colSpan={7} style={{ textAlign: "center", padding: 24, color: "var(--mui-palette-text-secondary)" }}>Nenhum artigo</td></tr>
					)}
				</tbody>
			</table>
		</section>
	);
};

// ── Categories admin: drag-drop reorder + parent change ─────────────
const CategoriesAdmin: React.FC<{
	workspaceId: string;
	categories: PublicKbCategoryDTO[];
	onEdit: (c: PublicKbCategoryDTO) => void;
	onNew: () => void;
	onRefresh: () => void;
}> = ({ workspaceId, categories, onEdit, onNew, onRefresh }) => {
	const [draggingId, setDraggingId] = useState<string | null>(null);
	const flat = categories.flatMap((c) => flatCats(c));

	const handleDrop = async (draggedId: string, targetId: string | null, asChild: boolean) => {
		// Calcula novo parentId + nova ordem
		const dragged = flat.find((c) => c.id === draggedId);
		if (!dragged) return;
		const target = targetId ? flat.find((c) => c.id === targetId) : null;
		// Anti-ciclo: nao permite mover sob descendente
		if (target && isDescendantOf(target, draggedId, flat)) {
			NotistackServices.showErrorMessage("Movimento invalido (criaria ciclo)");
			return;
		}
		const newParentId = asChild ? targetId : target?.parentId ?? null;
		const siblings = flat.filter((c) => c.parentId === newParentId && c.id !== draggedId);
		let entries: Array<{ id: string; parentId: string | null; order: number }>;
		if (asChild) {
			entries = [...siblings, dragged].map((c, i) => ({ id: c.id, parentId: newParentId, order: i }));
		} else {
			const targetIdx = siblings.findIndex((s) => s.id === targetId);
			const insertAt = targetIdx >= 0 ? targetIdx + 1 : siblings.length;
			const newOrder = [...siblings];
			newOrder.splice(insertAt, 0, dragged);
			entries = newOrder.map((c, i) => ({ id: c.id, parentId: newParentId, order: i }));
		}
		try {
			await KnowledgeBaseServices.reorderCategories(workspaceId, entries);
			onRefresh();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao reordenar");
		}
	};

	return (
		<section className={styles.manageSection}>
			<div className={styles.manageHeader}>
				<p style={{ margin: 0, fontSize: "0.85rem", color: "var(--mui-palette-text-secondary)" }}>
					Arraste <Icon path={mdiDragVertical} size={0.6} /> p/ reordenar. Solte SOBRE uma categoria para torná-la filha; ENTRE categorias p/ reordenar.
				</p>
				<Button size="small" variant="contained" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={onNew}>Nova</Button>
			</div>
			<div className={styles.categoryTree}>
				{flat.length === 0 ? (
					<p className={styles.emptyText}>Nenhuma categoria.</p>
				) : flat.map((c, idx) => (
					<React.Fragment key={c.id}>
						<div
							className={styles.dropZone}
							onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add(styles.dropZoneActive); }}
							onDragLeave={(e) => e.currentTarget.classList.remove(styles.dropZoneActive)}
							onDrop={(e) => {
								e.preventDefault();
								e.currentTarget.classList.remove(styles.dropZoneActive);
								if (draggingId) handleDrop(draggingId, idx === 0 ? null : flat[idx - 1].id, false);
							}}
						/>
						<div
							className={styles.categoryRow}
							draggable
							onDragStart={() => setDraggingId(c.id)}
							onDragEnd={() => setDraggingId(null)}
							onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add(styles.dropOnto); }}
							onDragLeave={(e) => e.currentTarget.classList.remove(styles.dropOnto)}
							onDrop={(e) => {
								e.preventDefault();
								e.currentTarget.classList.remove(styles.dropOnto);
								if (draggingId && draggingId !== c.id) handleDrop(draggingId, c.id, true);
							}}
							style={{ paddingLeft: 12 + c._depth * 24 }}
						>
							<Icon path={mdiDragVertical} size={0.7} className={styles.dragHandle} />
							<Icon path={mdiFolderOutline} size={0.8} />
							<span className={styles.catName}>{c.name}</span>
							<span className={styles.catSlug}>{c.slug}</span>
							<div className={styles.catActions}>
								<IconButton size="small" onClick={() => onEdit(c)}><Icon path={mdiPencil} size={0.7} /></IconButton>
								<IconButton size="small" onClick={async () => {
									if (!window.confirm(`Excluir "${c.name}"?`)) return;
									await KnowledgeBaseServices.deleteCategory(workspaceId, c.id);
									onRefresh();
								}}><Icon path={mdiDelete} size={0.7} /></IconButton>
							</div>
						</div>
					</React.Fragment>
				))}
				{/* Final drop zone (root, at end) */}
				{flat.length > 0 && (
					<div
						className={styles.dropZone}
						onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add(styles.dropZoneActive); }}
						onDragLeave={(e) => e.currentTarget.classList.remove(styles.dropZoneActive)}
						onDrop={(e) => {
							e.preventDefault();
							e.currentTarget.classList.remove(styles.dropZoneActive);
							if (draggingId) handleDrop(draggingId, flat[flat.length - 1].id, false);
						}}
					/>
				)}
			</div>
		</section>
	);
};

function isDescendantOf(target: PublicKbCategoryDTO & { _depth: number }, ancestorId: string, all: (PublicKbCategoryDTO & { _depth: number })[]): boolean {
	let cur: any = target;
	while (cur?.parentId) {
		if (cur.parentId === ancestorId) return true;
		cur = all.find((c) => c.id === cur.parentId);
	}
	return false;
}

// ── Tags admin ─────────────────────────────────────────────────────
const TagsAdmin: React.FC<{
	workspaceId: string;
	tags: PublicKbTagDTO[];
	onEdit: (t: PublicKbTagDTO) => void;
	onNew: () => void;
	onRefresh: () => void;
}> = ({ workspaceId, tags, onEdit, onNew, onRefresh }) => (
	<section className={styles.manageSection}>
		<div className={styles.manageHeader}>
			<p style={{ margin: 0, fontSize: "0.85rem", color: "var(--mui-palette-text-secondary)" }}>Clique p/ editar, X p/ excluir.</p>
			<Button size="small" variant="contained" startIcon={<Icon path={mdiPlus} size={0.7} />} onClick={onNew}>Nova</Button>
		</div>
		<div className={styles.tagList}>
			{tags.length === 0 ? (
				<p className={styles.emptyText}>Nenhuma tag ainda.</p>
			) : tags.map((t) => (
				<Chip
					key={t.id}
					label={t.name}
					style={{ backgroundColor: t.color ?? undefined }}
					onClick={() => onEdit(t)}
					onDelete={async () => {
						if (!window.confirm(`Excluir tag "${t.name}"?`)) return;
						await KnowledgeBaseServices.deleteTag(workspaceId, t.id);
						onRefresh();
					}}
				/>
			))}
		</div>
	</section>
);

function flatCats(cat: PublicKbCategoryDTO, depth = 0): (PublicKbCategoryDTO & { _depth: number })[] {
	const self = { ...cat, _depth: depth };
	const children = (cat.children ?? []).flatMap((c) => flatCats(c, depth + 1));
	return [self, ...children];
}

// ── Editor Dialog (article | category | tag) ───────────────────────
const EditorDialog: React.FC<{
	workspaceId: string;
	editing: { kind: "article" | "category" | "tag"; data?: any };
	categories: PublicKbCategoryDTO[];
	tags: PublicKbTagDTO[];
	onClose: () => void;
	onSaved: () => void;
}> = ({ workspaceId, editing, categories, tags, onClose, onSaved }) => {
	const isEdit = !!editing.data;
	const [form, setForm] = useState<any>(editing.data ?? {});
	const [saving, setSaving] = useState(false);

	const save = async () => {
		setSaving(true);
		try {
			if (editing.kind === "article") {
				if (isEdit) {
					await KnowledgeBaseServices.updateArticle(workspaceId, form.id, {
						title: form.title,
						content: form.content,
						excerpt: form.excerpt,
						categoryId: form.categoryId ?? null,
						tagIds: form.tagIds ?? (form.tags ?? []).map((t: any) => t.id),
					});
				} else {
					await KnowledgeBaseServices.createArticle(workspaceId, {
						title: form.title,
						content: form.content,
						excerpt: form.excerpt,
						categoryId: form.categoryId ?? null,
						status: form.status ?? "draft",
						tagIds: form.tagIds ?? [],
					});
				}
			} else if (editing.kind === "category") {
				if (isEdit) {
					await KnowledgeBaseServices.updateCategory(workspaceId, form.id, {
						name: form.name,
						description: form.description,
						parentId: form.parentId ?? null,
					});
				} else {
					await KnowledgeBaseServices.createCategory(workspaceId, {
						name: form.name,
						description: form.description,
						parentId: form.parentId ?? null,
					});
				}
			} else if (editing.kind === "tag") {
				if (isEdit) {
					await KnowledgeBaseServices.updateTag(workspaceId, form.id, { name: form.name, color: form.color });
				} else {
					await KnowledgeBaseServices.createTag(workspaceId, { name: form.name, color: form.color });
				}
			}
			NotistackServices.showSuccessMessage("Salvo");
			onSaved();
		} catch (e) {
			NotistackServices.showErrorMessage(HandleError.isHandleError(e) ? e.message : "Erro ao salvar");
		} finally {
			setSaving(false);
		}
	};

	const isArticle = editing.kind === "article";
	return (
		<Dialog open onClose={onClose} maxWidth={isArticle ? "lg" : "md"} fullWidth fullScreen={isArticle}>
			<DialogTitle>{isEdit ? "Editar" : "Novo"} {editing.kind === "article" ? "artigo" : editing.kind === "category" ? "categoria" : "tag"}</DialogTitle>
			<DialogContent>
				<div className={styles.formGrid}>
					{editing.kind === "article" && (
						<>
							<TextField label="Título" size="small" fullWidth value={form.title ?? ""} onChange={(e) => setForm({ ...form, title: e.target.value })} />
							<TextField label="Excerpt (resumo)" size="small" fullWidth multiline rows={2} value={form.excerpt ?? ""} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
							<div className={styles.editorSplit}>
								<TextField
									label="Content (markdown)"
									size="small"
									fullWidth
									multiline
									minRows={20}
									value={form.content ?? ""}
									onChange={(e) => setForm({ ...form, content: e.target.value })}
									className={styles.editorTextArea}
								/>
								<div className={styles.editorPreview}>
									<div className={styles.editorPreviewLabel}>Preview</div>
									<div className={styles.articleBody}>
										<ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content ?? "_Vazio. Digite markdown na esquerda._"}</ReactMarkdown>
									</div>
								</div>
							</div>
							<Select size="small" displayEmpty value={form.categoryId ?? ""} onChange={(e) => setForm({ ...form, categoryId: e.target.value || null })}>
								<MenuItem value="">Sem categoria</MenuItem>
								{categories.map((c) => <MenuItem key={c.id} value={c.id}>{"— ".repeat((c as any)._depth ?? 0)}{c.name}</MenuItem>)}
							</Select>
							<Select size="small" multiple value={form.tagIds ?? (form.tags ?? []).map((t: any) => t.id)} onChange={(e) => setForm({ ...form, tagIds: e.target.value as string[] })}>
								{tags.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
							</Select>
							<Select size="small" value={form.status ?? "draft"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
								<MenuItem value="draft">Draft</MenuItem>
								<MenuItem value="published">Published</MenuItem>
								<MenuItem value="archived">Archived</MenuItem>
							</Select>
						</>
					)}
					{editing.kind === "category" && (
						<>
							<TextField label="Nome" size="small" fullWidth value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
							<TextField label="Descrição" size="small" fullWidth multiline rows={2} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
							<Select size="small" displayEmpty value={form.parentId ?? ""} onChange={(e) => setForm({ ...form, parentId: e.target.value || null })}>
								<MenuItem value="">Sem parent (raiz)</MenuItem>
								{categories.filter((c) => c.id !== form.id).map((c) => <MenuItem key={c.id} value={c.id}>{"— ".repeat((c as any)._depth ?? 0)}{c.name}</MenuItem>)}
							</Select>
						</>
					)}
					{editing.kind === "tag" && (
						<>
							<TextField label="Nome" size="small" fullWidth value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
							<TextField label="Cor (#RRGGBB)" size="small" fullWidth value={form.color ?? ""} placeholder="#1DB954" onChange={(e) => setForm({ ...form, color: e.target.value })} />
						</>
					)}
				</div>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>Cancelar</Button>
				<Button variant="contained" onClick={save} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
			</DialogActions>
		</Dialog>
	);
};
